import { Search, useTheme } from "@deere/fuel-react";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { Box, Theme, Typography } from "@mui/material";

import {
  DataGridPro,
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Dispatch, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import NoDataAvailableError from "../../../../../components/DataGridPro/ErrorPage/components/NoDataAvailableError/NoDataAvailableError";
import NoDataAvailableForFilterError from "../../../../../components/DataGridPro/ErrorPage/components/NoDataAvailableForFilterError/NoDataAvailableForFilterError";
import TechnicalError from "../../../../../components/DataGridPro/ErrorPage/components/TechnicalError/TechnicalError";
import DateRangePicker from "../../../../../components/DatePickers/DateRangePicker/DateRangePicker";
import LoadingIndicator from "../../../../../components/LoadingIndicator/LoadingIndicator";
import { generateDataGridLocaleTexts } from "../../../../../utils/dataGridUtils/utils";
import ResourceLink from "../../../../orders/components/ResourceLink/ResourceLink";
import { generateGridDefaultStyles } from "../../../../orders/OrdersByLineItem/utils/table";
import { reorderColumns } from "../../../../orders/utils";
import {
  MyLocatorInventoryActions,
  setFilterAction,
} from "../../reducers/actions";
import { MyLocatorInventoryFilters } from "../../types";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface MyLocatorInventoryItem {
  id: string | number;
  line: number;
  partNumber: string;
  quantity: number;
  dateTimeUpdated: string;
}

export interface MyLocatorInventoryTableProps {
  inventory: {
    inventoryItems: MyLocatorInventoryItem[];
    totalInventoryItems: number;
  };
  loading: boolean;
  showAuthenticationError: boolean;
  showTechnicalError: boolean;
  sort: {
    sortModel: GridSortModel;
    updateSortModel: (model: GridSortModel) => void;
  };
  filter: {
    myLocatorInventoryFilters: MyLocatorInventoryFilters;
    isFilterApplied: boolean;
  };
  dispatch: Dispatch<MyLocatorInventoryActions>;
}

function DateUpdatedCell({ date }: { date: string }) {
  const formattedDate = useMemo(() => {
    if (!date) return { date: "", time: "" };
    const dateObj = dayjs(date);
    return {
      date: dateObj.format("DD-MMM-YYYY"),
      time: dateObj.format("ddd, h:mmA"),
    };
  }, [date]);

  const theme = useTheme();

  return (
    <Box data-testid="date-updated-cell">
      <Typography
        data-testid="date"
        sx={{
          fontWeight: theme.tokens.FuelFontWeightRegular,
          fontSize: theme.tokens.FuelFontSizeHeading5,
          lineHeight: theme.tokens.FuelLineHeightSm,
        }}
      >
        {formattedDate.date}
      </Typography>
      <Typography
        sx={{
          fontSize: theme.tokens.FuelFontSizeHeading5,
          color: theme.tokens.FuelColorTextTertiary,
          lineHeight: theme.tokens.FuelLineHeightSm,
        }}
        data-testid="time"
      >
        {formattedDate.time}
      </Typography>
    </Box>
  );
}

const shouldShowNoDataAvailableError = (
  dataLength: number,
  isFilterApplied: boolean,
  showTechnicalError: boolean,
  showAuthenticationError: boolean
) =>
  dataLength === 0 &&
  !isFilterApplied &&
  !showTechnicalError &&
  !showAuthenticationError;

const shouldShowNoDataAvailableForFilterError = (
  dataLength: number,
  isFilterApplied: boolean,
  showTechnicalError: boolean,
  showAuthenticationError: boolean
) =>
  dataLength === 0 &&
  isFilterApplied &&
  !showTechnicalError &&
  !showAuthenticationError;

const generatePartNumberColumn = (
  t: any,
  theme: Theme,
  filters: MyLocatorInventoryFilters,
  dispatch: Dispatch<MyLocatorInventoryActions>
): GridColDef => ({
  field: "partNumber",
  headerName: t(
    "d2d:myLocatorInventory.table.header.partNumber",
    "Part Number"
  ),
  renderCell: (params: GridRenderCellParams) => (
    <ResourceLink value={params.value} url={`/parts/${params.value}`} />
  ),
  renderHeaderFilter: () => (
    <Search
      placeholder={t("common:searchHere", "Search here...")}
      value={filters.partNumber || ""}
      onChange={(e) =>
        dispatch(
          setFilterAction({
            ...filters,
            partNumber: e.target.value,
          })
        )
      }
      onClear={() =>
        dispatch(
          setFilterAction({
            ...filters,
            partNumber: undefined,
          })
        )
      }
      data-testid="part-number-filter"
      sx={{ width: "100%" }}
    />
  ),
});

const generateQuantityColumn = (
  t: any,
  theme: Theme,
  filters: MyLocatorInventoryFilters,
  dispatch: Dispatch<MyLocatorInventoryActions>
): GridColDef => ({
  field: "quantity",
  headerName: t("d2d:myLocatorInventory.table.header.quantity", "Quantity"),
  renderCell: (params: GridRenderCellParams) => (
    <Typography
      sx={{
        fontSize: theme.tokens.FuelFontSizeSm,
        fontWeight: theme.tokens.FuelFontWeightRegular,
      }}
      data-testid={`quantity-cell-${params.value}`}
    >
      {params.value}
    </Typography>
  ),
  renderHeaderFilter: () => (
    <Search
      placeholder={t("common:searchHere", "Search here...")}
      value={filters.quantity || ""}
      onChange={(e) =>
        dispatch(
          setFilterAction({
            ...filters,
            quantity: e.target.value,
          })
        )
      }
      onClear={() =>
        dispatch(
          setFilterAction({
            ...filters,
            quantity: undefined,
          })
        )
      }
      data-testid="quantity-filter"
      sx={{ width: "100%" }}
    />
  ),
});

const generateDateTimeUpdatedColumn = (
  t: any,
  theme: Theme,
  filters: MyLocatorInventoryFilters,
  dispatch: Dispatch<MyLocatorInventoryActions>
): GridColDef => ({
  field: "dateTimeUpdated",
  headerName: t(
    "d2d:myLocatorInventory.table.header.dateTimeUpdated",
    "Date/Time Updated"
  ),
  renderCell: (params: GridRenderCellParams) => (
    <DateUpdatedCell date={params.value} />
  ),
  renderHeaderFilter: () => (
    <DateRangePicker
      value={[
        filters.dateTimeUpdatedFrom || null,
        filters.dateTimeUpdatedTo || null,
      ]}
      onChange={(selection) => {
        const [startDate, endDate] = selection;
        dispatch(
          setFilterAction({
            ...filters,
            dateTimeUpdatedFrom: startDate,
            dateTimeUpdatedTo: endDate,
          })
        );
      }}
      label=""
      enableShortcuts
      dateRangeCalendarProps={{
        minDate: dayjs().subtract(1, "year"),
        maxDate: dayjs(),
      }}
      textFieldProps={{
        placeholder: t(
          "common:dateRangePicker.placeholder",
          "Start Date - End Date"
        ),
        size: "small",
        sx: {
          width: "100%",
          ".MuiInputBase-input": {
            fontSize: "0.875rem",
            padding: "8.5px 14px",
          },
        },
      }}
    />
  ),
});

const generateLineColumn = (t: any, theme: Theme): GridColDef => ({
  field: "line",
  headerName: t("d2d:myLocatorInventory.table.header.line", "Line"),
  filterable: false,
  renderCell: (params: GridRenderCellParams) => (
    <Typography
      sx={{
        fontSize: theme.tokens.FuelFontSizeSm,
        fontWeight: theme.tokens.FuelFontWeightRegular,
      }}
      data-testid={`line-cell-${params.value}`}
    >
      {params.value}
    </Typography>
  ),
});

const MyLocatorInventoryTable = ({
  inventory,
  loading,
  showAuthenticationError,
  showTechnicalError,
  sort,
  filter,
  dispatch,
}: MyLocatorInventoryTableProps) => {
  const { sortModel } = sort;
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);
  const [rows, setRows] = useState<MyLocatorInventoryItem[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  useEffect(() => {
    if (loading) {
      setRows([]);
    } else {
      setRows(inventory.inventoryItems);
    }
  }, [inventory.inventoryItems, loading]);
  useEffect(() => {
    const lineColumn = generateLineColumn(t, theme);
    const partNumberColumn = generatePartNumberColumn(
      t,
      theme,
      filter.myLocatorInventoryFilters,
      dispatch
    );
    const quantityColumn = generateQuantityColumn(
      t,
      theme,
      filter.myLocatorInventoryFilters,
      dispatch
    );
    const dateTimeUpdatedColumn = generateDateTimeUpdatedColumn(
      t,
      theme,
      filter.myLocatorInventoryFilters,
      dispatch
    );

    const columnDefs = [
      lineColumn,
      partNumberColumn,
      quantityColumn,
      dateTimeUpdatedColumn,
    ].map((column) => ({
      ...column,
      headerAlign: "left" as const,
      align: "left" as const,
      minWidth: 175,
      flex: 1,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading6,
          }}
        >
          {params.colDef.headerName}
        </Typography>
      ),
      sortable: true,
    }));

    setColumns(columnDefs);
  }, [theme, t, filter.myLocatorInventoryFilters, dispatch]);

  const hiddenFields = ["__tree_data_group__"];

  const getTogglableColumns = (gridColumns: GridColDef[]) =>
    gridColumns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );

  const groupingColumnEnabled = false;

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <DataGridPro
        localeText={generateDataGridLocaleTexts(t)}
        autoHeight
        data-testid="my-locator-inventory-table"
        sx={generateGridDefaultStyles(theme)}
        style={{ borderRadius: "10px" }}
        treeData={groupingColumnEnabled}
        columnBufferPx={columns.length + 1}
        rows={rows}
        columns={columns}
        hideFooter
        getRowHeight={() => "auto"}
        headerFilters={
          filter.isFilterApplied || inventory.inventoryItems.length > 0
        }
        headerFilterHeight={60}
        loading={loading}
        disableColumnMenu
        disableRowSelectionOnClick
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => sort.updateSortModel(model)}
        onColumnOrderChange={({ targetIndex, oldIndex }) =>
          setColumns((prevState) =>
            reorderColumns(prevState, oldIndex, targetIndex)
          )
        }
        slots={{
          columnUnsortedIcon: ImportExportIcon,
          columnSortedAscendingIcon: NorthIcon,
          columnSortedDescendingIcon: SouthIcon,
          loadingOverlay: LoadingIndicator,
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {shouldShowNoDataAvailableForFilterError(
                  inventory.inventoryItems.length,
                  filter.isFilterApplied,
                  showTechnicalError,
                  showAuthenticationError
                ) && <NoDataAvailableForFilterError />}
                {shouldShowNoDataAvailableError(
                  inventory.inventoryItems.length,
                  filter.isFilterApplied,
                  showTechnicalError,
                  showAuthenticationError
                ) && (
                  <NoDataAvailableError subtitleTranslationKey="myLocatorInventory" />
                )}
                {showAuthenticationError && <TechnicalError />}
                {showTechnicalError && <TechnicalError />}
              </Box>
            </Box>
          ),
        }}
        slotProps={{
          columnsManagement: { getTogglableColumns },
        }}
      />
    </Box>
  );
};

export default MyLocatorInventoryTable;
